/**
 * CourtsSection - Courts naming section for scroll wizard
 */
import React, { useState, useEffect, useRef } from 'react';
import { useTournament } from '../../contexts/TournamentContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import firebaseSyncService from '../../services/firebaseSync';
import ConfirmModal from '../../components/modals/ConfirmModal';

const CourtsSection = ({ onGenerate }) => {
  const { config, courts: savedCourts, setCourts: saveCourts, generateSchedule, exportState, scheduleGenerated } = useTournament();
  const { currentUser, isSignedIn } = useAuth();
  const { showSuccess, showError } = useToast();
  const [courts, setCourts] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const originalCourtName = useRef(null);
  const lastSyncedCourtsRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    if (config && !initialized) {
      initializeCourts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, initialized]);

  // Watch for changes in courtCount and update courts accordingly
  useEffect(() => {
    if (config && initialized && config.courtCount !== courts.length) {
      updateCourtsCount(config.courtCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.courtCount, initialized]);

  // Sync local state when savedCourts changes externally (reset, import)
  useEffect(() => {
    if (!savedCourts || savedCourts.length === 0) return;
    const contextJson = JSON.stringify(savedCourts);
    if (lastSyncedCourtsRef.current !== null && contextJson !== lastSyncedCourtsRef.current) {
      setCourts(savedCourts);
      lastSyncedCourtsRef.current = contextJson;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedCourts]);

  const updateCourtsCount = (newCourtCount) => {
    const currentCourts = [...courts];

    if (newCourtCount > currentCourts.length) {
      // Add new courts
      for (let i = currentCourts.length; i < newCourtCount; i++) {
        currentCourts.push({
          id: i,
          name: `Terrain ${i + 1}`
        });
      }
    } else if (newCourtCount < currentCourts.length) {
      // Remove excess courts
      currentCourts.splice(newCourtCount);
    }

    setCourts(currentCourts);
    saveCourts(currentCourts);
    lastSyncedCourtsRef.current = JSON.stringify(currentCourts);
  };

  const initializeCourts = () => {
    const courtCount = config.courtCount;
    const newCourts = [];

    // Check if we have existing courts to restore
    if (savedCourts && savedCourts.length === courtCount) {
      // Restore existing courts
      newCourts.push(...savedCourts);
    } else {
      // Create new courts with default names
      for (let i = 0; i < courtCount; i++) {
        newCourts.push({
          id: i,
          name: `Terrain ${i + 1}`
        });
      }
    }

    setCourts(newCourts);

    // Save to context only on first initialization
    if (!initialized) {
      saveCourts(newCourts);
      lastSyncedCourtsRef.current = JSON.stringify(newCourts);
      setInitialized(true);
    }
  };

  const handleCourtNameChange = (index, name) => {
    const newCourts = [...courts];
    newCourts[index] = {
      ...newCourts[index],
      name: name
    };
    setCourts(newCourts);
  };

  const handleCourtNameBlur = async (index) => {
    if (courts[index].name === originalCourtName.current) return;

    const trimmed = courts[index].name.trim();
    const isDuplicate = courts.some((c, i) =>
      i !== index && c.name.trim().toLowerCase() === trimmed.toLowerCase()
    );

    if (isDuplicate) {
      const newCourts = [...courts];
      newCourts[index] = { ...newCourts[index], name: originalCourtName.current };
      setCourts(newCourts);
      showError(`Le nom "${trimmed}" est déjà utilisé par un autre terrain.`);
      return;
    }

    saveCourts(courts);
    lastSyncedCourtsRef.current = JSON.stringify(courts);

    if (isSignedIn() && currentUser) {
      try {
        const tournamentState = exportState();
        tournamentState.courts = courts;
        await firebaseSyncService.saveTournament(tournamentState, currentUser.email, 'court-renamed');
      } catch (error) {
        console.warn('⚠️ Could not save court name to Firebase:', error);
      }
    }

    showSuccess('Terrain modifié', 1500);
  };

  const handleInputFocus = (e, index) => {
    e.target.select();
    originalCourtName.current = courts[index].name;
  };

  const serializeSchedule = (rawSchedule) => ({
    ...rawSchedule,
    rounds: rawSchedule.rounds.map(round => ({
      ...round,
      matches: round.matches.map(match => ({
        ...match,
        detailedScore: match.detailedScore ? match.detailedScore.toJSON() : null
      }))
    }))
  });

  const handleGenerate = async () => {
    setGenerating(true);

    try {
      const result = await generateSchedule();

      // Save to Firebase using the fresh result directly (avoids stale closure on exportState)
      if (isSignedIn() && currentUser && result.schedule) {
        try {
          const tournamentState = exportState();
          tournamentState.schedule = serializeSchedule(result.schedule);
          tournamentState.scheduleGenerated = true;
          await firebaseSyncService.saveTournament(tournamentState, currentUser.email, 'planning-generated');
          await firebaseSyncService.markPlanningAsGenerated(currentUser.email);
          console.log('✅ Planning final sauvegardé dans Firebase');
          console.log('📋 Tournoi généré - ID:', firebaseSyncService.currentTournamentId, '| Nom:', config.tournamentName || 'Sans nom', '| rounds:', result.schedule.rounds.length);
        } catch (error) {
          console.warn('⚠️ Could not save planning to Firebase:', error);
        }
      }

      setTimeout(() => {
        setGenerating(false);
        if (onGenerate) onGenerate();
      }, 1000);
    } catch (error) {
      console.error('Error generating schedule:', error);
      setGenerating(false);
    }
  };

  const handleRegenerateClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmRegenerate = async () => {
    setGenerating(true);

    try {
      const result = await generateSchedule();

      // Save to Firebase using the fresh result directly (avoids stale closure on exportState)
      if (isSignedIn() && currentUser && result.schedule) {
        try {
          const tournamentState = exportState();
          tournamentState.schedule = serializeSchedule(result.schedule);
          tournamentState.scheduleGenerated = true;
          await firebaseSyncService.saveTournament(tournamentState, currentUser.email, 'planning-regenerated');
          await firebaseSyncService.markPlanningAsGenerated(currentUser.email);
          console.log('✅ Planning re-généré et sauvegardé dans Firebase');
          console.log('📋 Tournoi re-généré - ID:', firebaseSyncService.currentTournamentId, '| Nom:', config.tournamentName || 'Sans nom', '| rounds:', result.schedule.rounds.length);
        } catch (error) {
          console.warn('⚠️ Could not save regenerated planning to Firebase:', error);
        }
      }

      setTimeout(() => {
        setGenerating(false);
      }, 1000);
    } catch (error) {
      console.error('Error regenerating schedule:', error);
      setGenerating(false);
    }
  };

  if (!config) {
    return (
      <div className="section-inner">
        <div className="section-header">
          <h1>
            <i className="bi bi-grid-3x3 me-3"></i>
            Terrains
          </h1>
        </div>
        <div className="section-body">
          <p className="text-center text-muted">Veuillez d'abord configurer le tournoi.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="section-inner">
      <div className="section-header">
        <h1>
          <i className="bi bi-grid-3x3 me-3"></i>
          Terrains
        </h1>
      </div>

      <div className="section-body">
        <div className="courts-list">
          {courts.map((court, index) => (
            <div
              key={court.id}
              className="court-row mb-3 p-3"
            >
              <div className="d-flex align-items-center gap-3">
                {/* Court Icon */}
                <span className="badge bg-success d-flex align-items-center gap-1">
                  <i className="bi bi-grid-3x3"></i> {index + 1}
                </span>

                {/* Court Name Input */}
                <div className="court-name-input flex-grow-1">
                  <input
                    type="text"
                    className="form-control"
                    id={`court-${index}`}
                    value={court.name || ''}
                    onChange={(e) => handleCourtNameChange(index, e.target.value)}
                    onBlur={() => handleCourtNameBlur(index)}
                    onFocus={(e) => handleInputFocus(e, index)}
                    required
                    maxLength={30}
                    autoComplete="off"
                    placeholder={`Terrain ${index + 1}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Generate button */}
        <div className="generate-section text-center mt-4">
          {scheduleGenerated ? (
            <button
              type="button"
              className="btn btn-lg btn-generate"
              onClick={handleRegenerateClick}
              disabled={generating}
            >
              {generating ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Re-génération en cours...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-repeat me-2"></i>
                  Re-générer le planning
                </>
              )}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-lg btn-generate"
                onClick={handleGenerate}
                disabled={generating || config.playerCount < 4}
              >
                {generating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Génération en cours...
                  </>
                ) : (
                  <>
                    <i className="bi bi-arrow-right me-2"></i>
                    Générer le planning optimal
                  </>
                )}
              </button>

              {config.playerCount < 4 && (
                <div className="alert alert-warning mt-3">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Minimum 4 joueurs requis pour générer le planning
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmRegenerate}
        title="Re-générer le planning"
        message="Êtes-vous sûr de vouloir re-générer un nouveau planning ? Cette action va remplacer le planning actuel."
        confirmLabel="Re-générer"
        cancelLabel="Annuler"
        confirmVariant="warning"
        icon="bi-exclamation-triangle"
      />
    </div>
  );
};

export default CourtsSection;
